import React, { useState } from 'react';
import { HelpCircle, Book, RotateCcw, Eye, EyeOff, X } from 'lucide-react';
import { useTutorial } from '../../contexts/TutorialContext';
import { useTranslation } from 'react-i18next';

/**
 * HeaderHelpButton Component - ปุ่มช่วยเหลือสำหรับ header
 */
const HeaderHelpButton = () => {
  const { 
    startOnboarding, 
    showTooltips, 
    toggleTooltips, 
    hasCompletedOnboarding 
  } = useTutorial();
  const { i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleRestartTour = () => {
    setIsMenuOpen(false);
    startOnboarding();
  };

  const handleToggleTooltips = () => {
    toggleTooltips();
  };

  return (
    <div className="relative">
      {/* Help Button */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="relative flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
        aria-label={i18n.language === 'th' ? 'ความช่วยเหลือ' : 'Help'}
        title={i18n.language === 'th' ? 'ความช่วยเหลือ' : 'Help'}
      >
        <HelpCircle className="h-4 w-4" />
        <span className="hidden sm:inline">{i18n.language === 'th' ? 'ช่วยเหลือ' : 'Help'}</span>
        
        {/* Pulse indicator for new users */}
        {!hasCompletedOnboarding && (
          <span className="absolute top-1 right-1 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
          </span>
        )}
      </button>

      {/* Help Menu Dropdown */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-slideDown z-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <Book className="h-5 w-5" />
                  <h3 className="font-semibold">
                    {i18n.language === 'th' ? 'ศูนย์ช่วยเหลือ' : 'Help Center'}
                  </h3>
                </div>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                  aria-label={i18n.language === 'th' ? 'ปิด' : 'Close'}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-2">
              {/* Restart Tour */}
              <button
                onClick={handleRestartTour}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors text-left group"
              >
                <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-800 transition-colors">
                  <RotateCcw className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {i18n.language === 'th' ? 'เริ่มทัวร์ใหม่' : 'Restart Tour'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {i18n.language === 'th' 
                      ? 'ดูคำแนะนำการใช้งานอีกครั้ง' 
                      : 'View usage guide again'}
                  </div>
                </div>
              </button>

              {/* Toggle Tooltips */}
              <button
                onClick={handleToggleTooltips}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left group"
              >
                <div className="h-10 w-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-800 transition-colors">
                  {showTooltips ? (
                    <Eye className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {showTooltips 
                      ? (i18n.language === 'th' ? 'ปิดคำแนะนำ' : 'Hide Tooltips')
                      : (i18n.language === 'th' ? 'แสดงคำแนะนำ' : 'Show Tooltips')
                    }
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {showTooltips
                      ? (i18n.language === 'th' ? 'ซ่อน tooltips ทั้งหมด' : 'Hide all tooltips')
                      : (i18n.language === 'th' ? 'แสดง tooltips ทั้งหมด' : 'Show all tooltips')
                    }
                  </div>
                </div>
              </button>

              {/* Documentation Link (if available) */}
              <a
                href="/knowledge-center"
                onClick={() => setIsMenuOpen(false)}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left group"
              >
                <div className="h-10 w-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-800 transition-colors">
                  <Book className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {i18n.language === 'th' ? 'คู่มือการใช้งาน' : 'Documentation'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {i18n.language === 'th' 
                      ? 'เรียนรู้เพิ่มเติม' 
                      : 'Learn more'}
                  </div>
                </div>
              </a>
            </div>

            {/* Footer */}
            <div className="p-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                {i18n.language === 'th' 
                  ? 'คลิกปุ่มช่วยเหลือได้ทุกเมื่อ' 
                  : 'Click help button anytime'}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HeaderHelpButton;

